import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import { GET_FEDERAL_DOCUMENT_STATUSES } from '../graphql/documents.graphql';
import {
  FederalDocumentStatuses,
  GetFederalDocumentStatusesQuery,
  GetFederalDocumentStatusesQueryVariables,
} from '../graphql/types';

type GetFederalDocumentStatusesType =
  | {
      documentStatuses: FederalDocumentStatuses;
      loading: false;
    }
  | {
      documentStatuses: null;
      loading: true;
    };

type GetFederalDocumentStatusesTypeWithSkip =
  | {
      documentStatuses: null;
      loading: false;
    }
  | GetFederalDocumentStatusesType;

export interface UseGetFederalDocumentStatusesOptions {
  skip?: boolean;
  fiscalYearId?: string;
}

export const useGetFederalDocumentStatuses = ({
  fiscalYearId,
  skip,
}: UseGetFederalDocumentStatusesOptions): GetFederalDocumentStatusesTypeWithSkip => {
  const shouldSkip = skip || !fiscalYearId;
  const { data, error, loading } = useQuery<GetFederalDocumentStatusesQuery, GetFederalDocumentStatusesQueryVariables>(
    GET_FEDERAL_DOCUMENT_STATUSES,
    {
      variables: { fiscalYearId: fiscalYearId! },
      skip: shouldSkip,
    },
  );

  if (shouldSkip) {
    return { documentStatuses: null, loading: false };
  }

  if (loading) {
    return { documentStatuses: null, loading: true };
  }

  if (error) {
    throw error;
  }

  if (!data) {
    throw createError('MissingDocumentStatuses', 'Failed to load document statuses');
  }

  return {
    documentStatuses: data?.getFederalDocumentStatuses,
    loading,
  };
};
